import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import Dialog from '@mui/material/Dialog';
import { IProductSchema } from '../../../../../graphql/type/product';
import { useAllowDropshippingProductsMutation } from '../../../../../graphql/mutation/product/allowDropshippingProductsMutation';
import BaseButton from '../../../../components/_button/_module/BaseButton';
import useNotification from '../../../../components/_hook/useNotification';
import FormAllowDropshipping from './FormAllowDropshipping';
import BaseIconButton from '../../../../components/_iconButton/_module/BaseIconButton';

interface IProps {
  products: IProductSchema[];
  onCompleted?: () => void;
  component?: (onClickAllowDropshipping: () => void) => ReactElement;
  iconButton?: boolean;
}

export default function ButtonAllowDropshipping(props: IProps) {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { onCompleted, component, products, iconButton } = props;
  const [isShowFormAllowDropshipping, setIsFormAllowDropshipping] = useState(
    false
  );
  const [productsWithoutPrice, setProductsWithoutPrice] = useState<
    IProductSchema[]
  >([]);

  const [
    allowDropshippingProductsMutation,
    { loading: isAllowingDropshippingProducts },
  ] = useAllowDropshippingProductsMutation(
    `updatedProducts {
				_id
				dropshipping {
				  enable
				}
			}`,
    {
      onCompleted: (data) => {
        if (
          data &&
          data.allowDropshippingProducts &&
          data.allowDropshippingProducts.updatedProducts
        ) {
          const count = data.allowDropshippingProducts.updatedProducts.length;
          notification.success(
            `${count} ${t('product', { count })} ${t(
              'allow_dropshipping_done'
            )}`
          );
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  function toggleFormAllowDropshipping() {
    setIsFormAllowDropshipping(!isShowFormAllowDropshipping);
  }

  async function allowDropshipping(selectedProductWithPrice: IProductSchema[]) {
    if (selectedProductWithPrice.length) {
      await allowDropshippingProductsMutation({
        variables: {
          _ids: selectedProductWithPrice.map((product) => product._id),
        },
      });
    }
  }

  async function onClickAllowDropshipping() {
    if (products.length) {
      const selectedProductWithoutPrice = products.filter(
        (product) =>
          !product.prices || !product.prices.currency || !product.prices.final
      );
      const selectedProductWithPrice = products.filter(
        (product) =>
          product.prices && product.prices.currency && product.prices.final
      );

      await allowDropshipping(selectedProductWithPrice);

      if (!selectedProductWithoutPrice.length) {
        if (onCompleted) {
          onCompleted();
        }
      } else {
        setProductsWithoutPrice(selectedProductWithoutPrice);
        toggleFormAllowDropshipping();
      }
    }
  }

  return (
    <>
      <Dialog open={isShowFormAllowDropshipping} maxWidth="sm" fullWidth>
        <FormAllowDropshipping
          onClose={toggleFormAllowDropshipping}
          onCompleted={() => {
            toggleFormAllowDropshipping();
            if (onCompleted) {
              onCompleted();
            }
          }}
          productIDs={productsWithoutPrice.map((product) => product._id)}
        />
      </Dialog>
      {iconButton ? (
        <BaseIconButton
          onClick={onClickAllowDropshipping}
          tooltip={t('disallow_dropshipping')}
          loading={isAllowingDropshippingProducts}
          size="small"
        >
          <MoveToInboxIcon fontSize="small" />
        </BaseIconButton>
      ) : (
        <>
          {component ? (
            component(onClickAllowDropshipping)
          ) : (
            <BaseButton
              color="primary"
              variant="contained"
              size="small"
              tooltip={t('allow_dropshipping')}
              onClick={onClickAllowDropshipping}
              startIcon={
                <MoveToInboxIcon htmlColor="#F92949" fontSize="small" />
              }
            >
              {t('allow_dropshipping')}
            </BaseButton>
          )}
        </>
      )}
    </>
  );
}
